import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vehix/portal/components/ui/tabs';
import { Button } from '@vehix/portal/components/ui/button';
import { Label } from '@vehix/portal/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { Play, Calendar, Plus } from 'lucide-react';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';
import { PipelineDashboard } from './PipelineDashboard';
import { PipelineCreationDialog } from './PipelineCreationDialog';
import { PipelineSchedulesList } from './PipelineSchedulesList';
import { PipelineScheduleFormDialog } from './PipelineScheduleFormDialog';
import type { PipelineSchedule } from '../../services/pipelineOrchestrator';

// eslint-disable-next-line max-lines-per-function
export const PipelineOrchestratorTab: React.FC = () => {
  // Sub-tab lives in the URL rather than component state so /workflow/pipelines
  // ?tab=schedules is linkable — the old standalone Schedules page redirects
  // here, and without this it would always land on Runs. Runs stays the default
  // and writes no param, so the bare URL is unchanged.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSubTab: 'runs' | 'schedules' =
    searchParams.get('tab') === 'schedules' ? 'schedules' : 'runs';

  const setActiveSubTab = useCallback(
    (value: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (value === 'schedules') next.set('tab', 'schedules');
          else next.delete('tab');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [selectedTenant, setSelectedTenant] = useState<string>('__all__');

  // Dialog states
  const [creationDialogOpen, setCreationDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleToEdit, setScheduleToEdit] = useState<PipelineSchedule | null>(null);

  // Tenant data
  const { data: tenants } = useAllTenants();

  // Default tenant scope is "All Tenants" — admins running pipelines need
  // cross-tenant visibility by default rather than being silently scoped to
  // their own primary tenant. The dropdown still lets them narrow to one
  // tenant when they want to. (Previously a useEffect overrode the initial
  // '__all__' to currentTenant.id on first load, which caused a flicker AND
  // hid cross-tenant runs from admins who came here to see them.)
  const actualTenantId = selectedTenant === '__all__' ? undefined : selectedTenant;

  // Schedule edit handler
  const handleEditSchedule = (schedule: PipelineSchedule) => {
    setScheduleToEdit(schedule);
    setScheduleDialogOpen(true);
  };

  const handleCreateSchedule = () => {
    setScheduleToEdit(null);
    setScheduleDialogOpen(true);
  };

  return (
    <div className='space-y-5'>
      {/* Tenant filter + Action button. Page-level title removed — sidebar
          nav already labels this page as "Pipelines", and the sub-tabs
          below ("Runs" / "Schedules") name the active view. Matches the
          Setup-pages convention of dropping redundant in-page headers
          (commit 4ef7286e). */}
      <div className='flex items-center justify-end gap-3'>
        <div className='flex items-center gap-2'>
          <Label className='text-xs text-muted-foreground'>Tenant:</Label>
          <Select value={selectedTenant} onValueChange={setSelectedTenant}>
            <SelectTrigger className='w-[180px] h-8 text-xs'>
              <SelectValue placeholder='All tenants' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='__all__'>All Tenants</SelectItem>
              {(tenants || []).map(t => (
                <SelectItem key={t.id} value={t.id}>
                  {t.tenant_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {activeSubTab === 'runs' && (
          <Button size='sm' onClick={() => setCreationDialogOpen(true)}>
            <Play className='h-3.5 w-3.5 mr-1' />
            Run Pipeline
          </Button>
        )}
        {activeSubTab === 'schedules' && (
          <Button size='sm' onClick={handleCreateSchedule}>
            <Plus className='h-3.5 w-3.5 mr-1' />
            Create Schedule
          </Button>
        )}
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className='bg-transparent h-10 p-0 space-x-4 border-b w-full justify-start'>
          <TabsTrigger
            value='runs'
            className={
              'h-full rounded-none border-b-2 border-transparent ' +
              'data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 pb-2'
            }
          >
            <Play className='h-3.5 w-3.5 mr-1.5' />
            Runs
          </TabsTrigger>
          <TabsTrigger
            value='schedules'
            className={
              'h-full rounded-none border-b-2 border-transparent ' +
              'data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 pb-2'
            }
          >
            <Calendar className='h-3.5 w-3.5 mr-1.5' />
            Schedules
          </TabsTrigger>
        </TabsList>

        <TabsContent value='runs' className='mt-4'>
          <PipelineDashboard tenantId={actualTenantId} tenants={tenants} />
        </TabsContent>

        <TabsContent value='schedules' className='mt-4'>
          <PipelineSchedulesList tenantId={actualTenantId} onEdit={handleEditSchedule} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <PipelineCreationDialog
        open={creationDialogOpen}
        onOpenChange={setCreationDialogOpen}
        defaultTenantId={actualTenantId}
      />

      <PipelineScheduleFormDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        schedule={scheduleToEdit}
        defaultTenantId={actualTenantId}
      />
    </div>
  );
};
